import { getOperatorLicensesWithCompetitionNames } from '@/components/LicenseListFilters/LicenseListFilters.helpers'
import { getOperatorPropertyLicenses } from '@/service/licensesService'
import type { LicensePropertyData } from '@/stores/licenses'
import type { FormattedOperatorWithLicense } from '@/types/operator'

export type LicensePeriodRow = {
  operator: string
  operatorId: number
  property: string
  id: number
  startDate: string
  endDate: string
  suspended: boolean
  unlimited: boolean
  numOfEvents: number
  numOfConsumedEvents: number
  numOfBookedEvents: number
  type: string
  competitionIds: string
}

export const csvHeaders = [
  'Operator',
  'Operator ID',
  'Property',
  'Licence Period ID',
  'Start Date',
  'End Date',
  'Suspended',
  'Unlimited',
  'No. of Events',
  'No. of Consumed Events',
  'No. of Booked Events',
  'All competitions wildcard',
  'Competition Names'
]

export const formatCsvRow = (row: LicensePeriodRow): string[] => {
  return [
    row.operator || '',
    row.operatorId?.toString() || '',
    row.property || '',
    row.id?.toString() || '',
    row.startDate || '',
    row.endDate || '',
    row.suspended ? 'True' : 'False',
    row.unlimited ? 'True' : 'False',
    row.numOfEvents?.toString() || '0',
    row.numOfConsumedEvents?.toString() || '0',
    row.numOfBookedEvents?.toString() || '0',
    row.type || '',
    row.competitionIds || ''
  ]
}

export const augmentLicenseDataWithPeriods = async (
  licenseRows: LicensePropertyData[]
): Promise<LicensePeriodRow[]> => {
  if (!licenseRows.length) return []

  const selectedProperties = [...new Set(licenseRows.map((row) => row.property))]

  try {
    // we need to access the data for all historical uses of selected licenses, so no matter that we only selected a few properties - we still need all the data to filter through
    const allLicenses = await getOperatorPropertyLicenses('all')

    let enrichedLicenses: FormattedOperatorWithLicense[]
    try {
      enrichedLicenses = await getOperatorLicensesWithCompetitionNames(allLicenses)
    } catch (error) {
      console.error('Failed to enrich competition names:', error)
      enrichedLicenses = allLicenses
    }

    const filteredPeriods: LicensePeriodRow[] = []

    for (const selectedProperty of selectedProperties) {
      const matchingPeriods = enrichedLicenses.flatMap((operator) =>
        operator.license
          .filter((license) =>
            license.property.toLowerCase().includes(selectedProperty.toLowerCase())
          )
          .flatMap(
            (license) =>
              license.licensingPeriods?.map((period) => ({
                operator: operator.name,
                operatorId: operator.id,
                property: license.property.toString(),
                id: period.id || 0,
                startDate: period.startDate || '',
                endDate: period.endDate || '',
                suspended: period.suspended,
                unlimited: period.unlimited,
                numOfEvents: period.numOfEvents || 0,
                numOfBookedEvents: period.numOfBookedEvents || 0,
                numOfConsumedEvents: period.numOfConsumedEvents || 0,
                type: period.subscription?.type || '',
                competitionIds: period.subscription?.competitionIds?.join(', ') || ''
              })) || []
          )
      )

      filteredPeriods.push(...matchingPeriods)
    }

    return filteredPeriods
  } catch (error) {
    console.error('Failed to fetch license data for augmentation:', error)
    return []
  }
}
